import { submitForm as hookSurveysSubmit } from './HookSurveys';
import { submitForm as hookEventsSubmit, useFormSubmission } from './HookEvents';
import { submitForm as hookInquiriesSubmit } from './HookInquiries';
import { submitForm as hookOffersSubmit } from './HookOffers';
import { submitForm as hookBookingsSubmit } from './HookBookings';
import { submitForm as hookVotingsSubmit } from './HookVotings';
import { submitForm as hookRedeemsSubmit } from './HookRedeems';
import { submitForm as memberSubmit } from './Member';

// Main handler that determines which controller-specific handler to use
export const submitForm = async (submitButton, form, options) => {
    const { controller } = options;
    // Call checkVoting for HookVotings before proceeding with AJAX validation
    if ((controller === 'HookVotings' || controller === 'hook-votings') && typeof window.checkVoting === 'function') {
        if (!window.checkVoting(submitButton)) {
            return { success: false, error: 'Voting validation failed' };
        }
    }
    
    console.log(`Form submission using ${controller} controller handler`);

    const handlers = [
        { keys: ['hook-events', 'HookEvents'], handler: hookEventsSubmit },
        { keys: ['hook-surveys', 'HookSurveys'], handler: hookSurveysSubmit },
        { keys: ['hook-inquiries', 'HookInquiries'], handler: hookInquiriesSubmit },
        { keys: ['hook-offers', 'HookOffers'], handler: hookOffersSubmit },
        { keys: ['hook-bookings', 'HookBookings'], handler: hookBookingsSubmit },
        { keys: ['hook-votings', 'HookVotings'], handler: hookVotingsSubmit },
        { keys: ['hook-redeems', 'HookRedeems'], handler: hookRedeemsSubmit },
        { keys: ['member', 'Member'], handler: memberSubmit }
    ];

    const resolveHandler = () => {
        for (const { keys, handler } of handlers) {
            if (keys.includes(controller)) {
                return handler;
            }
        }
        console.error(`No handler found for controller: ${controller}`);
        return hookEventsSubmit;
    };

    const ensureTurnstileToken = async () => {
        const needsTurnstile = options.turnstileRequired || window.__form_turnstile_required__;
        if (!needsTurnstile) {
            return true;
        }
        if (typeof window.__ensure_turnstile_token__ !== 'function') {
            return false;
        }
        try {
            const token = await window.__ensure_turnstile_token__();
            if (token) {
                options.turnstileToken = token;
                return true;
            }
        } catch (e) {
        }
        return false;
    };

    const runHandler = async () => {
        if (typeof window.__clear_form_honeypot__ === 'function') {
            window.__clear_form_honeypot__();
        }
        if (!(await ensureTurnstileToken())) {
            return {
                success: false,
                message: 'Security verification failed. Please try again.'
            };
        }
        const handler = resolveHandler();
        return handler(submitButton, form, options);
    };

    let result = await runHandler();
    if (!result.success && result.require_turnstile) {
        if (typeof window.__set_turnstile_required__ === 'function') {
            window.__set_turnstile_required__();
        }
        options.turnstileRequired = true;
        await new Promise((resolve) => setTimeout(resolve, 800));
        if (await ensureTurnstileToken()) {
            result = await runHandler();
        }
    }
    return result;
};

// Export individual handlers directly for components that need direct access
export const submitHookEvents = hookEventsSubmit;
export const submitHookSurveys = hookSurveysSubmit;
export const submitHookInquiries = hookInquiriesSubmit;
export const submitHookOffers = hookOffersSubmit;
export const submitHookBookings = hookBookingsSubmit;
export const submitHookVotings = hookVotingsSubmit;
export const submitHookRedeems = hookRedeemsSubmit;
export const submitMember = memberSubmit;
export { useFormSubmission };

// Initialize form submission for a specific controller type
export const initializeFormSubmission = (controllerType, funnelPageId) => {
    console.log(`Initializing form submission for ${controllerType}`);
    
    if (!window.checkFormHooks) {
        window.checkFormHooks = {};
    }
    
    // Map controller types to their submission handlers
    const handlers = {
        'HookEvents': hookEventsSubmit,
        'HookSurveys': hookSurveysSubmit,
        'HookInquiries': hookInquiriesSubmit,
        'HookOffers': hookOffersSubmit,
        'HookBookings': hookBookingsSubmit,
        'HookVotings': hookVotingsSubmit,
        'HookRedeems': hookRedeemsSubmit,
        'Member': memberSubmit
    };
    
    // Register the specific handler
    if (handlers[controllerType]) {
        window.checkFormHooks[controllerType] = {
            submitForm: handlers[controllerType]
        };
        console.log(`Registered ${controllerType} form submission handler`);
    } else {
        console.error(`No handler available for ${controllerType}`);
    }
};

// Make initializeFormSubmission available globally
if (typeof window !== 'undefined') {
    window.initializeFormSubmission = initializeFormSubmission;
}

export default {
    submitForm,
    submitHookEvents: hookEventsSubmit,
    submitHookSurveys: hookSurveysSubmit,
    submitHookInquiries: hookInquiriesSubmit,
    submitHookOffers: hookOffersSubmit,
    submitHookBookings: hookBookingsSubmit,
    submitHookVotings: hookVotingsSubmit,
    submitHookRedeems: hookRedeemsSubmit,
    submitMember: memberSubmit,
    useFormSubmission,
    initializeFormSubmission,
    controllers: {
        hookEvents: { submitForm: hookEventsSubmit },
        hookSurveys: { submitForm: hookSurveysSubmit },
        hookInquiries: { submitForm: hookInquiriesSubmit },
        hookOffers: { submitForm: hookOffersSubmit },
        hookBookings: { submitForm: hookBookingsSubmit },
        hookVotings: { submitForm: hookVotingsSubmit },
        hookRedeems: { submitForm: hookRedeemsSubmit },
        member: { submitForm: memberSubmit }
    }
}; 